/**
 * Триангуляция фундаментальной области и адресация уровней детализации (LOD).
 *
 * Поскольку геометрия неевклидова, фундаментальная область разбивается не на
 * квадраты, а на треугольники, которые рекурсивно подразбиваются с учётом
 * кривизны — по **гиперболическим серединам** сторон (см. {@link midpoint}),
 * а не линейно в экранных координатах (Требование 3.5).
 *
 * Схема:
 * 1. **База (уровень 0):** правильный многоугольник разбивается веером из своего
 *    центра на `n` треугольников — по одному на каждую сторону
 *    (`центр + два конца стороны`). Для правильного многоугольника, центрированного
 *    в начале координат, центр совпадает с центром диска (началом координат).
 * 2. **Подразбиение (уровень L→L+1):** каждый треугольник делится на 4 соединением
 *    гиперболических середин его трёх сторон: три угловых подтреугольника плюс один
 *    центральный. Порядок потомков задокументирован в {@link subdivideTriangle}.
 * 3. **Адресация:** треугольник идентифицируется путём `(rootIndex, [0..3]*)` —
 *    индекс корневого треугольника плюс последовательность выборов потомка. По пути
 *    детерминированно восстанавливаются вершины (см. {@link triangleFromPath}).
 * 4. **Число уровней** — функция масштаба карты (см. {@link maxLevelForScale}).
 *
 * Все функции чистые и детерминированные, без DOM.
 *
 * Требования: 3.1, 3.2, 3.5
 */

import type { Complex, FundamentalPolygon } from './map-types.js';
import type { Geometry } from './geometry.js';
import { midpoint } from './hyperbolic.js';

/**
 * Треугольник на поверхности — упорядоченная тройка вершин `[v0, v1, v2]`
 * в координатах модели Пуанкаре (диска). Порядок вершин значим: он определяет
 * детерминированное подразбиение и адресацию потомков.
 */
export type Triangle = [Complex, Complex, Complex];

/**
 * Адрес треугольника в дереве триангуляции.
 * `rootIndex` — индекс корневого треугольника базового веера (`0..n−1`),
 * `childPath` — последовательность выборов потомка (`0..3`) при спуске по уровням.
 * Длина `childPath` равна уровню детализации треугольника.
 */
export interface TrianglePath {
  /** Индекс корневого треугольника базового веерного разбиения. */
  rootIndex: number;
  /** Последовательность выборов потомка `[0..3]` при спуске по уровням. */
  childPath: number[];
}

/**
 * Базовое число уровней подразбиения, добавляемое всегда (даже при `scale = 0`).
 * Обеспечивает минимальную тесселяцию геодезических у базовой триангуляции.
 */
export const BASE_LEVELS = 2;

/**
 * Абсолютный верхний предел глубины подразбиения. Ограничивает объём геометрии
 * и, соответственно, максимальное приближение независимо от масштаба карты
 * (Требования 3.2, 4.5).
 */
export const MAX_ABSOLUTE_LEVEL = 12;

/**
 * Центр правильного многоугольника в модели Пуанкаре.
 *
 * Для правильного многоугольника, центрированного в начале координат (как его
 * строит {@link buildRegularHyperbolicPolygon}), вершины симметричны относительно
 * начала, поэтому их среднее равно центру диска (началу координат). Функция
 * возвращает евклидово среднее вершин: для симметричного случая это ровно `0`,
 * а для смещённого — разумное приближение центра.
 *
 * @param polygon фундаментальный многоугольник
 * @returns центр (барицентр) многоугольника
 */
export function polygonCenter(polygon: FundamentalPolygon): Complex {
  const n = polygon.vertices.length;
  if (n === 0) {
    return { re: 0, im: 0 };
  }
  let re = 0;
  let im = 0;
  for (const v of polygon.vertices) {
    re += v.re;
    im += v.im;
  }
  return { re: re / n, im: im / n };
}

/**
 * Базовое веерное разбиение (уровень 0): по одному треугольнику на каждую сторону
 * многоугольника, `[центр, v_k, v_{k+1}]`, `k = 0..n−1` (индекс `k+1` берётся по
 * модулю `n`, замыкая последнюю сторону на первую вершину).
 *
 * @param polygon фундаментальный многоугольник (`≥ 3` вершины)
 * @returns массив из `n` корневых треугольников
 */
export function buildBaseTriangulation(polygon: FundamentalPolygon): Triangle[] {
  const vertices = polygon.vertices;
  const n = vertices.length;
  if (n < 3) {
    throw new Error(`Polygon must have at least 3 vertices, got ${n}`);
  }
  const center = polygonCenter(polygon);
  const triangles: Triangle[] = [];
  for (let k = 0; k < n; k++) {
    const a = vertices[k];
    const b = vertices[(k + 1) % n];
    triangles.push([{ ...center }, { ...a }, { ...b }]);
  }
  return triangles;
}

/**
 * Подразбить треугольник на 4 по гиперболическим серединам его сторон.
 *
 * Пусть треугольник — `[v0, v1, v2]`. Обозначим середину стороны, противолежащей
 * вершине `vi`, как `mi` (гиперболическая середина, {@link midpoint}):
 * - `m0 = midpoint(v1, v2)` — противолежит `v0`;
 * - `m1 = midpoint(v2, v0)` — противолежит `v1`;
 * - `m2 = midpoint(v0, v1)` — противолежит `v2`.
 *
 * Порядок потомков (документированная конвенция):
 * - **child 0** — угловой при `v0`: `[v0, m2, m1]`;
 * - **child 1** — угловой при `v1`: `[v1, m0, m2]`;
 * - **child 2** — угловой при `v2`: `[v2, m1, m0]`;
 * - **child 3** — центральный: `[m0, m1, m2]`.
 *
 * Угловой потомок `i` использует вершину `vi` и середины двух сторон, смежных с
 * `vi`. Все середины вычисляются гиперболически, поэтому лежат на геодезических
 * сторонах, а потомки вложены в родителя (Требование 3.5).
 *
 * @param tri родительский треугольник
 * @returns четыре потомка в порядке `[corner0, corner1, corner2, center]`
 */
export function subdivideTriangle(
  tri: Triangle,
  geometry?: Geometry,
): [Triangle, Triangle, Triangle, Triangle] {
  const mid = geometry ? (a: Complex, b: Complex): Complex => geometry.midpoint(a, b) : midpoint;
  const [v0, v1, v2] = tri;
  const m0 = mid(v1, v2);
  const m1 = mid(v2, v0);
  const m2 = mid(v0, v1);
  return [
    [{ ...v0 }, { ...m2 }, { ...m1 }],
    [{ ...v1 }, { ...m0 }, { ...m2 }],
    [{ ...v2 }, { ...m1 }, { ...m0 }],
    [{ ...m0 }, { ...m1 }, { ...m2 }],
  ];
}

/**
 * Восстановить вершины треугольника по его адресу `(rootIndex, childPath)`.
 *
 * Спуск детерминирован: берётся корневой треугольник `buildBaseTriangulation(polygon)[rootIndex]`,
 * затем для каждого индекса `c ∈ childPath` треугольник подразбивается и выбирается
 * потомок `c` (см. {@link subdivideTriangle}). Одинаковый путь всегда даёт один и тот
 * же треугольник (детерминизм адресации).
 *
 * @param polygon фундаментальный многоугольник
 * @param path адрес треугольника
 * @returns вершины адресуемого треугольника
 * @throws при выходе `rootIndex`/элемента `childPath` за допустимый диапазон
 */
export function triangleFromPath(
  polygon: FundamentalPolygon,
  path: TrianglePath,
  geometry?: Geometry,
): Triangle {
  const roots = buildBaseTriangulation(polygon);
  if (path.rootIndex < 0 || path.rootIndex >= roots.length) {
    throw new Error(
      `rootIndex ${path.rootIndex} out of range [0, ${roots.length})`,
    );
  }
  let tri = roots[path.rootIndex];
  for (const child of path.childPath) {
    if (child < 0 || child > 3) {
      throw new Error(`child index ${child} out of range [0, 3]`);
    }
    tri = subdivideTriangle(tri, geometry)[child];
  }
  return tri;
}

/**
 * Все треугольники на заданном равномерном уровне подразбиения.
 *
 * Уровень `0` — базовый веер; каждый следующий уровень заменяет каждый треугольник
 * его четырьмя потомками. На уровне `L` получается `n · 4^L` треугольников.
 *
 * @param polygon фундаментальный многоугольник
 * @param level уровень подразбиения (`≥ 0`)
 * @returns массив треугольников уровня `level`
 */
export function trianglesAtLevel(
  polygon: FundamentalPolygon,
  level: number,
  geometry?: Geometry,
): Triangle[] {
  if (level < 0) {
    throw new Error(`level must be ≥ 0, got ${level}`);
  }
  let current = buildBaseTriangulation(polygon);
  for (let l = 0; l < level; l++) {
    const next: Triangle[] = [];
    for (const tri of current) {
      next.push(...subdivideTriangle(tri, geometry));
    }
    current = next;
  }
  return current;
}

/**
 * Все адреса треугольников на заданном равномерном уровне подразбиения.
 *
 * Полезно для итерации по тайлам без немедленного вычисления их вершин
 * (вершины восстанавливаются лениво через {@link triangleFromPath}).
 *
 * @param rootCount число корневых треугольников (равно числу сторон многоугольника)
 * @param level уровень подразбиения (`≥ 0`)
 * @returns массив адресов; на уровне `L` — `rootCount · 4^L` элементов
 */
export function pathsAtLevel(rootCount: number, level: number): TrianglePath[] {
  if (rootCount < 1) {
    throw new Error(`rootCount must be ≥ 1, got ${rootCount}`);
  }
  if (level < 0) {
    throw new Error(`level must be ≥ 0, got ${level}`);
  }
  const paths: TrianglePath[] = [];
  for (let rootIndex = 0; rootIndex < rootCount; rootIndex++) {
    for (const childPath of enumerateChildPaths(level)) {
      paths.push({ rootIndex, childPath });
    }
  }
  return paths;
}

/**
 * Все последовательности выборов потомка `[0..3]` длины `depth`
 * (в лексикографическом порядке). При `depth = 0` — единственный пустой путь.
 */
function enumerateChildPaths(depth: number): number[][] {
  if (depth <= 0) {
    return [[]];
  }
  const shorter = enumerateChildPaths(depth - 1);
  const result: number[][] = [];
  for (const prefix of shorter) {
    for (let c = 0; c < 4; c++) {
      result.push([...prefix, c]);
    }
  }
  return result;
}

/**
 * Максимальный уровень подразбиения как функция масштаба карты `scale`.
 *
 * Отображение: `maxLevel = clamp(BASE_LEVELS + max(0, ⌊scale⌋), BASE_LEVELS, MAX_ABSOLUTE_LEVEL)`.
 * Каждая целая единица `scale` добавляет один уровень подразбиения (и, соответственно,
 * один шаг максимального приближения) поверх базовых {@link BASE_LEVELS}. Результат
 * зажимается сверху {@link MAX_ABSOLUTE_LEVEL}, чтобы ограничить объём геометрии
 * (Требования 3.2, 4.5).
 *
 * @param scale масштаб карты (нецелые/отрицательные значения приводятся вниз/к нулю)
 * @returns максимальный уровень детализации
 */
export function maxLevelForScale(scale: number): number {
  const extra = Number.isFinite(scale) ? Math.max(0, Math.floor(scale)) : 0;
  const level = BASE_LEVELS + extra;
  return Math.min(Math.max(level, BASE_LEVELS), MAX_ABSOLUTE_LEVEL);
}
